/**
 * PHASE 8: SyncDrawer Component
 * Slide-out drawer showing offline queue details and sync controls
 */

import { useState, useCallback } from 'react';
import { 
  X, 
  RefreshCw, 
  Trash2, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  CloudOff,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  XCircle,
  Cloud
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { retryQueueItem, discardQueueItem } from '@/lib/offline';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SyncStatusPill } from './SyncStatusPill';

// Action type display config
const ACTION_TYPE_CONFIG = {
  TIMECLOCK_PUNCH: {
    label: 'Time Clock',
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400'
  },
  AVAILABILITY_UPDATE: {
    label: 'Availability',
    icon: Clock,
    color: 'text-green-600 dark:text-green-400'
  },
  SCHEDULE_ACK: {
    label: 'Schedule',
    icon: CheckCircle,
    color: 'text-purple-600 dark:text-purple-400'
  },
  GENERIC_API_ACTION: {
    label: 'Action',
    icon: Cloud,
    color: 'text-slate-600 dark:text-slate-400'
  }
};

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    pending: { 
      label: 'Pending', 
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
    },
    syncing: { 
      label: 'Syncing', 
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
    },
    completed: { 
      label: 'Synced', 
      className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
    },
    failed: { 
      label: 'Failed', 
      className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
    },
    conflict: { 
      label: 'Conflict', 
      className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
    }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
      config.className
    )}>
      {config.label}
    </span>
  );
}

// Queue item component
function QueueItem({ item, onRetry, onDiscard, expanded, onToggle }) {
  const typeConfig = ACTION_TYPE_CONFIG[item.type] || ACTION_TYPE_CONFIG.GENERIC_API_ACTION;
  const Icon = typeConfig.icon;
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  const canRetry = item.status === 'failed' || item.status === 'conflict';
  const canDiscard = item.status !== 'syncing' && item.status !== 'completed';

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden transition-all",
      item.status === 'failed' && "border-red-200 dark:border-red-800",
      item.status === 'conflict' && "border-purple-200 dark:border-purple-800",
      item.status === 'pending' && "border-border",
      item.status === 'syncing' && "border-blue-200 dark:border-blue-800"
    )}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
      >
        <div className={cn(
          "flex items-center justify-center h-8 w-8 rounded-full",
          "bg-muted"
        )}>
          <Icon className={cn("h-4 w-4", typeConfig.color)} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{typeConfig.label}</span>
            <StatusBadge status={item.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {formatDate(item.createdAt)} at {formatTime(item.createdAt)}
          </p>
        </div>

        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t bg-muted/30">
          {/* Details */}
          <div className="pt-3 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Endpoint:</span>
              <span className="font-mono truncate max-w-[200px]">{item.endpoint}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Method:</span>
              <span className="font-mono">{item.method}</span>
            </div>
            {item.retryCount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Retry count:</span>
                <span>{item.retryCount}</span>
              </div>
            )}
            {item.lastError && (
              <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                <p className="font-medium text-xs mb-1">Error:</p>
                <p className="text-xs">{item.lastError}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {canRetry && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(item.id);
                }}
                className="flex-1"
              >
                <RotateCcw className="h-3 w-3 mr-1.5" />
                Retry
              </Button>
            )}
            {canDiscard && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onDiscard(item.id);
                }}
                className={cn(
                  "flex-1",
                  "text-red-600 hover:text-red-700 hover:bg-red-50",
                  "dark:text-red-400 dark:hover:bg-red-900/20"
                )}
              >
                <XCircle className="h-3 w-3 mr-1.5" />
                Discard
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SyncDrawer - Main drawer component
 */
export function SyncDrawer({ 
  trigger,
  open,
  onOpenChange
}) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    queueItems,
    syncNow,
    clearCompleted,
    refreshStats
  } = useOfflineSync();

  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const handleRetry = useCallback(async (id) => {
    setActionLoading(id);
    try {
      await retryQueueItem(id);
      await refreshStats();
      // Trigger sync if online
      if (isOnline) {
        syncNow();
      }
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setActionLoading(null);
    }
  }, [isOnline, refreshStats, syncNow]);

  const handleDiscard = useCallback(async (id) => {
    setActionLoading(id);
    try {
      await discardQueueItem(id);
      await refreshStats();
    } catch (error) {
      console.error('Discard failed:', error);
    } finally {
      setActionLoading(null);
    }
  }, [refreshStats]);

  const handleSyncNow = useCallback(async () => {
    await syncNow();
  }, [syncNow]);

  const handleClearCompleted = useCallback(async () => {
    await clearCompleted();
  }, [clearCompleted]);

  // Retry all failed items at once
  const [isRetryingAll, setIsRetryingAll] = useState(false);
  const handleRetryAllFailed = useCallback(async () => {
    const failedAndConflict = queueItems.filter(i => i.status === 'failed' || i.status === 'conflict');
    if (failedAndConflict.length === 0) return;
    
    setIsRetryingAll(true);
    try {
      // Retry all items in parallel
      await Promise.all(
        failedAndConflict.map(item => retryQueueItem(item.id))
      );
      await refreshStats();
      // Trigger sync if online
      if (isOnline) {
        syncNow();
      }
    } catch (error) {
      console.error('Retry all failed:', error);
    } finally {
      setIsRetryingAll(false);
    }
  }, [queueItems, isOnline, refreshStats, syncNow]);

  // Group items by status
  const pendingItems = queueItems.filter(i => i.status === 'pending' || i.status === 'syncing');
  const failedItems = queueItems.filter(i => i.status === 'failed');
  const conflictItems = queueItems.filter(i => i.status === 'conflict');
  const completedItems = queueItems.filter(i => i.status === 'completed');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-4 py-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              {isOnline ? (
                <Cloud className="h-5 w-5 text-green-600" />
              ) : (
                <CloudOff className="h-5 w-5 text-amber-600" />
              )}
              Sync Status
            </SheetTitle>
          </div>
          <SheetDescription>
            {!isOnline 
              ? 'You are offline. Changes will sync when you reconnect.'
              : isSyncing
              ? 'Syncing your changes...'
              : pendingCount > 0
              ? `${pendingCount} item${pendingCount !== 1 ? 's' : ''} waiting to sync`
              : 'All changes synced'
            }
          </SheetDescription>
        </SheetHeader>

        {/* Summary Stats */}
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">{pendingItems.length}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{failedItems.length}</p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{conflictItems.length}</p>
              <p className="text-xs text-muted-foreground">Conflicts</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{completedItems.length}</p>
              <p className="text-xs text-muted-foreground">Synced</p>
            </div>
          </div>
          
          {/* Retry All Failed Button */}
          {(failedItems.length > 0 || conflictItems.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetryAllFailed}
              disabled={isRetryingAll || !isOnline}
              className="w-full mt-3 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              {isRetryingAll ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Retrying All...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retry All Failed ({failedItems.length + conflictItems.length})
                </>
              )}
            </Button>
          )}
        </div>

        {/* Queue Items */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* Conflicts Section */}
            {conflictItems.length > 0 && (
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-purple-700 dark:text-purple-400">
                  <AlertTriangle className="h-4 w-4" />
                  Conflicts ({conflictItems.length})
                </h3>
                {conflictItems.map(item => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onRetry={handleRetry}
                    onDiscard={handleDiscard}
                  />
                ))}
              </div>
            )}

            {/* Failed Section */}
            {failedItems.length > 0 && (
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  Failed ({failedItems.length})
                </h3>
                {failedItems.map(item => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onRetry={handleRetry}
                    onDiscard={handleDiscard}
                  />
                ))}
              </div>
            )}

            {/* Pending Section */}
            {pendingItems.length > 0 && (
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-400">
                  <Clock className="h-4 w-4" />
                  Pending ({pendingItems.length})
                </h3>
                {pendingItems.map(item => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onRetry={handleRetry}
                    onDiscard={handleDiscard}
                  />
                ))}
              </div>
            )}

            {/* Completed Section */}
            {completedItems.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    Recently Synced ({completedItems.length})
                  </h3>
                  <button
                    onClick={handleClearCompleted}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear all
                  </button>
                </div>
                {completedItems.slice(0, 5).map(item => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onRetry={handleRetry}
                    onDiscard={handleDiscard}
                  />
                ))}
                {completedItems.length > 5 && (
                  <p className="text-xs text-center text-muted-foreground py-2">
                    +{completedItems.length - 5} more items
                  </p>
                )}
              </div>
            )}

            {/* Empty State */}
            {queueItems.length === 0 && (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="font-medium text-lg">All Synced!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  No pending actions in the queue.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <SheetFooter className="px-4 py-3 border-t bg-background">
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              onClick={refreshStats}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleSyncNow}
              disabled={!isOnline || isSyncing || pendingCount === 0}
              className="flex-1"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Cloud className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * SyncDrawerTrigger - Pre-styled trigger button
 */
export function SyncDrawerTrigger({ className }) {
  const [open, setOpen] = useState(false);
  
  return (
    <SyncDrawer
      open={open}
      onOpenChange={setOpen}
      trigger={
        <SyncStatusPill 
          onClick={() => setOpen(true)}
          className={className}
        />
      }
    />
  );
}

export default SyncDrawer;
